import { Controller } from "@hotwired/stimulus"

// § 3.9 Tier 1 Step 3 — slug-agnostic strategy/panel toggler.
//
// Originally QBO-only ("class" / "sub_customer" / "none"). Tiers 2/4
// will register additional per-provider panels (NetSuite has no
// strategy switch — additive dimensions; Sage Intacct same; BC has
// "none" / "shortcut_1" / "shortcut_2" / "global_dimension"). The
// controller doesn't hardcode any strategy slug — every panel target
// declares its own `data-strategy` value, and the strategy <select>
// is the source of truth for which panel is visible.
//
// Connect-time behavior:
//   1. If a strategy <select> target exists, sync visibility to its
//      current value.
//   2. If only a `strategyValue` is provided (server-rendered initial
//      strategy), use that.
//   3. Otherwise no-op — a form with no strategy switch and no panels
//      (e.g. NetSuite's purely additive dimension picker) just mounts
//      cleanly without raising in the browser console.
export default class extends Controller {
  static targets = ["strategy", "panel"]
  static values = { strategy: String }

  connect() {
    const initial = this._initialStrategy()
    if (initial !== null) this._sync(initial)
  }

  strategyChanged() {
    if (!this.hasStrategyTarget) return
    this._sync(this.strategyTarget.value)
  }

  _initialStrategy() {
    if (this.hasStrategyTarget) return this.strategyTarget.value
    if (this.strategyValue) return this.strategyValue
    return null
  }

  _sync(strategy) {
    if (!this.hasPanelTarget) return
    // § 3.9 Tier 5 Step 73 — visibility AND `required` need to track the
    // active strategy together: a hidden panel that still carries
    // `required` blocks form submit with an unfocusable-control error.
    // Stash the original required state on `data-required-original` once,
    // so toggling never loses it.
    this.panelTargets.forEach(p => {
      const visible = p.dataset.strategy === strategy
      p.classList.toggle("hidden", !visible)
      p.querySelectorAll("input, select, textarea").forEach(el => {
        if (el.dataset.requiredOriginal === undefined) {
          el.dataset.requiredOriginal = el.required ? "1" : "0"
        }
        el.required = visible && el.dataset.requiredOriginal === "1"
      })
    })
  }
}
